/**
 * Esquemas Zod del dominio del Optimizador de Cortes.
 *
 * El único formulario de la pantalla es el de **guardar una optimización** (nombre +
 * notas, ambos opcionales). El cálculo no es un formulario: la entrada (piezas/
 * tableros/config) se edita en tablas controladas y se valida al teclear (números
 * positivos), no con Zod. Los tipos se **infieren** del esquema
 * (`docs/frontend_conventions.md`, Validación).
 *
 * Los límites replican los del backend (`CutlistService.guardar_job`): el `nombre`
 * se recorta a 120 caracteres y las `notas` a 2000; aquí se acotan en el cliente para
 * dar feedback inmediato (el backend vuelve a recortar por seguridad).
 */
import { z } from "zod";

/** Longitud máxima del nombre del job (igual que el backend: `[:120]`). */
export const CUTLIST_NAME_MAX = 120;

/** Longitud máxima de las notas del job (igual que el backend: `[:2000]`). */
export const CUTLIST_NOTES_MAX = 2000;

/**
 * Formulario de guardado: nombre y notas opcionales. Se recortan a su máximo (en vez
 * de rechazar) para no bloquear el guardado por un texto largo; el backend hace lo
 * mismo. Ambos campos vacíos son válidos (el job se guarda sin nombre).
 */
export const saveCutlistSchema = z.object({
  nombre: z
    .string()
    .trim()
    .max(CUTLIST_NAME_MAX, `Máximo ${CUTLIST_NAME_MAX} caracteres.`)
    .optional(),
  notas: z
    .string()
    .trim()
    .max(CUTLIST_NOTES_MAX, `Máximo ${CUTLIST_NOTES_MAX} caracteres.`)
    .optional(),
});

/** Valores del formulario de guardado (inferidos del esquema). */
export type SaveCutlistForm = z.infer<typeof saveCutlistSchema>;
